import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChatTheme, Question } from "@/types/theme";
import { UnifiedChatWidget } from "@/components/chat/UnifiedChatWidget";
import { useState, useEffect, useCallback, useRef } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { useParams } from "react-router-dom";
import { useChatbotQueries } from "@/hooks/useChatbotQueries";
import { useChatbotMutations } from "@/hooks/useChatbotMutations";
import { OperatorSettings } from "./OperatorSettings";
import { useToast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { X } from "lucide-react";
import { ImageIcon } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { prepareThemeForDatabase } from "@/utils/themeUtils";

interface BuilderStyleProps {
  theme: ChatTheme;
  onUpdateTheme: (theme: ChatTheme) => void;
}

interface CompanyInfoFormValues {
  companyName: string;
  companyLogo?: string | null;
  tcpaLanguage?: string;
}

// Default theme values for reset functionality
const defaultTheme: ChatTheme = {
  id: 'default',
  chatbot_id: 'default',
  name: 'Default Theme',
  primarycolor: '#2563EB',
  backgroundcolor: '#FFFFFF',
  userbubblecolor: '#2563EB',
  botbubblecolor: '#F3F4F6',
  usertextcolor: '#FFFFFF',
  bottextcolor: '#1F2937',
  headercolor: '#2563EB',
  headertextcolor: '#FFFFFF',
  borderradius: '0.75rem',
  fontfamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  buttoncolor: '#2563EB',
  buttontextcolor: '#FFFFFF',
  position: 'right',
  buttontext: 'Chat with us',
  welcomebubblecolor: '#EFF6FF',
  welcomebubbletextcolor: '#1E40AF',
  inputbordercolor: '#E5E7EB',
  inputborderradius: '0.375rem',
  inputbackgroundcolor: '#FFFFFF',
  avatarbackgroundcolor: 'rgba(0,0,0,0.1)',
  headerbackgroundcolor: '#4FD1C5',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  showavatar: true,
  showtitle: true,
  showclosebutton: true,
  showreset: true,
  showsources: true,
  showtimestamp: true,
  showtypingindicator: true,
  showuseravatar: false
};

export const BuilderStyle = ({ theme, onUpdateTheme }: BuilderStyleProps) => {
  const [localTheme, setLocalTheme] = useState<ChatTheme>(theme);
  const { id: chatbotId } = useParams<{ id: string }>();
  const [previewTitle, setPreviewTitle] = useState("OfferChat Support");
  const {
    chatbot,
    transformedQuestions,
    transformedTriggerRules,
  } = useChatbotQueries(chatbotId);
  const { updateChatbot } = useChatbotMutations(chatbotId);
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [removeLogoOnSave, setRemoveLogoOnSave] = useState(false);

  // Delay settings state
  const [messageDelay, setMessageDelay] = useState(1000);
  const [buttonOptionsDelay, setButtonOptionsDelay] = useState(0);
  const [noResponseDelay, setNoResponseDelay] = useState(2000);
  const [optionsHandlerDelay, setOptionsHandlerDelay] = useState(10);

  // Debounce refs for delay settings
  const messageDelayTimeoutRef = useRef<NodeJS.Timeout>();
  const buttonOptionsDelayTimeoutRef = useRef<NodeJS.Timeout>();
  const noResponseDelayTimeoutRef = useRef<NodeJS.Timeout>();
  const optionsHandlerDelayTimeoutRef = useRef<NodeJS.Timeout>();

  const companyInfoForm = useForm<CompanyInfoFormValues>({
    defaultValues: {
      companyName: chatbot?.company_name || '',
      companyLogo: chatbot?.company_logo || null,
      tcpaLanguage: (chatbot?.settings as any)?.tcpaLanguage || ''
    }
  });

  useEffect(() => {
    setLocalTheme(theme);
    if (chatbot) {
      companyInfoForm.reset({
        companyName: chatbot.company_name || '',
        companyLogo: chatbot.company_logo || null,
        tcpaLanguage: (chatbot.settings as any)?.tcpaLanguage || ''
      });
      setCompanyLogoUrl(chatbot.company_logo || null);
      
      // Load delay settings from chatbot settings
      const settings = chatbot.settings as any || {};
      setMessageDelay(settings.messageDelay ?? 1000);
      setButtonOptionsDelay(settings.buttonOptionsDelay ?? 0);
      setNoResponseDelay(settings.noResponseDelay ?? 2000);
      setOptionsHandlerDelay(settings.optionsHandlerDelay ?? 10);
      
      // Load showTimestamp from chatbot settings and update local theme
      const showTimestamp = settings.showTimestamp ?? true;
      setLocalTheme(prev => ({
        ...prev,
        showTimestamp
      }));
    }
  }, [theme, chatbot, companyInfoForm]);

  useEffect(() => {
    if (chatbot?.title) {
      setPreviewTitle(chatbot.title);
    }
  }, [chatbot]);

  const handleColorChange = (key: keyof ChatTheme, value: string) => {
    const updatedTheme = {
      ...localTheme,
      [key]: value,
    };
    setLocalTheme(updatedTheme);
    onUpdateTheme(updatedTheme);
  };

  const handlePrimaryColorChange = (value: string) => {
    handleColorChange('primarycolor', value);
  };

  const handleBackgroundColorChange = (value: string) => {
    handleColorChange('backgroundcolor', value);
  };

  const handleUserBubbleColorChange = (value: string) => {
    handleColorChange('userbubblecolor', value);
  };

  const handleBotBubbleColorChange = (value: string) => {
    handleColorChange('botbubblecolor', value);
  };

  const handleUserTextColorChange = (value: string) => {
    handleColorChange('usertextcolor', value);
  };

  const handleBotTextColorChange = (value: string) => {
    handleColorChange('bottextcolor', value);
  };

  const handleHeaderColorChange = (value: string) => {
    handleColorChange('headercolor', value);
  };

  const handleHeaderTextColorChange = (value: string) => {
    handleColorChange('headertextcolor', value);
  };

  const handleBorderRadiusChange = (value: string) => {
    handleColorChange('borderradius', value);
  };

  const handleFontFamilyChange = (value: string) => {
    handleColorChange('fontfamily', value);
  };

  const handleTitleChange = (newTitle: string) => {
    setPreviewTitle(newTitle);
  };

  const handleResetToDefaults = () => {
    const resetTheme = { ...defaultTheme, id: localTheme.id, chatbot_id: localTheme.chatbot_id };
    setLocalTheme(resetTheme);
    onUpdateTheme(resetTheme);
    if (chatbot) {
      setPreviewTitle(chatbot.title || "OfferChat Support");
    }
  };

  const onCompanyInfoSubmit = async (data: CompanyInfoFormValues) => {
    if (!chatbotId) return;
    try {
      const updates: Record<string, any> = {
        company_name: data.companyName,
        settings: {
          ...(chatbot?.settings as any || {}),
          tcpaLanguage: data.tcpaLanguage || ''
        }
      };

      if (pendingLogoFile && !removeLogoOnSave) {
        setIsUploading(true);
        const file = pendingLogoFile;
        const fileExt = file.name.split('.').pop();
        const filePath = `${chatbotId}/company-logo-${uuidv4()}.${fileExt}`;

        // Delete the old logo file if it exists
        if (chatbot?.company_logo) {
          const urlParts = chatbot.company_logo.split('/chatbot-assets/');
          if (urlParts.length > 1) {
            const oldFilePath = urlParts[1];
            // Don't await this, continue even if deletion fails
            supabase.storage.from('chatbot-assets').remove([oldFilePath]).catch(console.warn);
          }
        }

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('chatbot-assets')
          .upload(filePath, file, {
            upsert: true,
            cacheControl: '3600'
          });

        if (uploadError) throw uploadError;

        const { data: publicUrlData } = supabase.storage
          .from('chatbot-assets')
          .getPublicUrl(filePath);

        updates.company_logo = publicUrlData.publicUrl;
        setCompanyLogoUrl(publicUrlData.publicUrl);
      } else if (removeLogoOnSave) {
        updates.company_logo = null;
        if (chatbot?.company_logo) {
          // Extract the file path from the full URL
          // URL format: https://domain.supabase.co/storage/v1/object/public/chatbot-assets/path
          const urlParts = chatbot.company_logo.split('/chatbot-assets/');
          if (urlParts.length > 1) {
            const filePath = urlParts[1]; // This will be: "chatbot-id/company-logo-uuid.ext"
            await supabase.storage.from('chatbot-assets').remove([filePath]);
          }
        }
      }

      await updateChatbot.mutateAsync(updates);

      setPendingLogoFile(null);
      setRemoveLogoOnSave(false);
      
      // Clear companyLogoUrl if logo was removed
      if (updates.company_logo === null) {
        setCompanyLogoUrl(null);
      }

      toast({
        title: "Company info saved",
        description: "Your company information has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: error.message || "Failed to save company information",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCompanyLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (!['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'].includes(file.type)) {
        toast({ variant: "destructive", title: "Invalid file type", description: "Please upload a JPG, PNG, GIF, or SVG." });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast({ variant: "destructive", title: "File too large", description: "Logo image must be less than 2MB." });
        return;
      }

      setPendingLogoFile(file);
      setRemoveLogoOnSave(false);

      const reader = new FileReader();
      reader.onload = (e) => {
        setCompanyLogoUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCompanyLogo = () => {
    setPendingLogoFile(null);
    setRemoveLogoOnSave(true);
    companyInfoForm.setValue('companyLogo', null);
    toast({
        title: "Logo marked for removal",
        description: "Save changes to remove the company logo.",
      });
  };

  const handleMessageDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setMessageDelay(delayValue);
    
    // Clear existing timeout
    if (messageDelayTimeoutRef.current) {
      clearTimeout(messageDelayTimeoutRef.current);
    }
    
    // Set new timeout to save after 500ms of no changes
    messageDelayTimeoutRef.current = setTimeout(() => {
      if (chatbotId) {
        updateChatbot.mutate({
          settings: {
            ...(chatbot?.settings as any || {}),
            messageDelay: delayValue
          }
        });
      }
    }, 500);
  }, [chatbotId, chatbot?.settings, updateChatbot]);

  const handleButtonOptionsDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setButtonOptionsDelay(delayValue);
    
    // Clear existing timeout
    if (buttonOptionsDelayTimeoutRef.current) {
      clearTimeout(buttonOptionsDelayTimeoutRef.current);
    }
    
    // Set new timeout to save after 500ms of no changes
    buttonOptionsDelayTimeoutRef.current = setTimeout(() => {
      if (chatbotId) {
        updateChatbot.mutate({
          settings: {
            ...(chatbot?.settings as any || {}),
            buttonOptionsDelay: delayValue
          }
        });
      }
    }, 500);
  }, [chatbotId, chatbot?.settings, updateChatbot]);

  const handleNoResponseDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setNoResponseDelay(delayValue);
    
    // Clear existing timeout
    if (noResponseDelayTimeoutRef.current) {
      clearTimeout(noResponseDelayTimeoutRef.current);
    }
    
    // Set new timeout to save after 500ms of no changes
    noResponseDelayTimeoutRef.current = setTimeout(() => {
      if (chatbotId) {
        updateChatbot.mutate({
          settings: {
            ...(chatbot?.settings as any || {}),
            noResponseDelay: delayValue
          }
        });
      }
    }, 500);
  }, [chatbotId, chatbot?.settings, updateChatbot]);

  const handleOptionsHandlerDelayChange = useCallback((value: number[]) => {
    const delayValue = value[0];
    setOptionsHandlerDelay(delayValue);
    
    // Clear existing timeout
    if (optionsHandlerDelayTimeoutRef.current) {
      clearTimeout(optionsHandlerDelayTimeoutRef.current);
    }
    
    // Set new timeout to save after 500ms of no changes
    optionsHandlerDelayTimeoutRef.current = setTimeout(() => {
      if (chatbotId) {
        updateChatbot.mutate({
          settings: {
            ...(chatbot?.settings as any || {}),
            optionsHandlerDelay: delayValue
          }
        });
      }
    }, 500);
  }, [chatbotId, chatbot?.settings, updateChatbot]);

  const handleTimestampToggle = (checked: boolean) => {
    const updatedTheme = {
      ...localTheme,
      showTimestamp: checked,
    };
    setLocalTheme(updatedTheme);
    
    // Save to chatbot settings
    if (chatbotId) {
      updateChatbot.mutate({
        settings: {
          ...(chatbot?.settings as any || {}),
          showTimestamp: checked
        }
      });
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (messageDelayTimeoutRef.current) clearTimeout(messageDelayTimeoutRef.current);
      if (buttonOptionsDelayTimeoutRef.current) clearTimeout(buttonOptionsDelayTimeoutRef.current);
      if (noResponseDelayTimeoutRef.current) clearTimeout(noResponseDelayTimeoutRef.current);
      if (optionsHandlerDelayTimeoutRef.current) clearTimeout(optionsHandlerDelayTimeoutRef.current);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Style Settings</h3>
        </div>

        <Tabs defaultValue="colors" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="colors">Colors & Appearance</TabsTrigger>
            <TabsTrigger value="operator">Operator Settings</TabsTrigger>
            <TabsTrigger value="company">Company Info</TabsTrigger>
            <TabsTrigger value="delays">Message Delays</TabsTrigger>
          </TabsList>

          <TabsContent value="colors" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Color & Appearance Settings</CardTitle>
                <CardDescription>Customize the visual appearance of your chat widget</CardDescription>
                <Button variant="outline" size="sm" onClick={handleResetToDefaults}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset to Defaults
              </Button>
              </CardHeader>
              <CardContent className="space-y-4">
             
                {/* Color inputs in a 3-column grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.primarycolor}
                        onChange={(e) => handlePrimaryColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.primarycolor}
                        onChange={(e) => handlePrimaryColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.backgroundcolor}
                        onChange={(e) => handleBackgroundColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.backgroundcolor}
                        onChange={(e) => handleBackgroundColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>User Bubble Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.userbubblecolor}
                        onChange={(e) => handleUserBubbleColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.userbubblecolor}
                        onChange={(e) => handleUserBubbleColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bot Bubble Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.botbubblecolor}
                        onChange={(e) => handleBotBubbleColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.botbubblecolor}
                        onChange={(e) => handleBotBubbleColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>User Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.usertextcolor}
                        onChange={(e) => handleUserTextColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.usertextcolor}
                        onChange={(e) => handleUserTextColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bot Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.bottextcolor}
                        onChange={(e) => handleBotTextColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.bottextcolor}
                        onChange={(e) => handleBotTextColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Header Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.headercolor}
                        onChange={(e) => handleHeaderColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.headercolor}
                        onChange={(e) => handleHeaderColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Header Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={localTheme.headertextcolor}
                        onChange={(e) => handleHeaderTextColorChange(e.target.value)}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={localTheme.headertextcolor}
                        onChange={(e) => handleHeaderTextColorChange(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Separate section for non-color inputs */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>Border Radius</Label>
                    <Input
                      type="text"
                      value={localTheme.borderradius}
                      onChange={(e) => handleBorderRadiusChange(e.target.value)}
                      placeholder="1rem"
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Font Family</Label>
                    <Select value={localTheme.fontfamily} onValueChange={handleFontFamilyChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select font family" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-apple-system, BlinkMacSystemFont, 'SF Pro', 'San Francisco', 'Helvetica Neue', Arial, sans-serif">SF Pro (iMessage)</SelectItem>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Arial">Arial</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Helvetica">Helvetica</SelectItem>
                        <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Verdana">Verdana</SelectItem>
                        <SelectItem value="system-ui">System UI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Show Timestamp: </Label><br></br>
                    <Switch checked={localTheme.showTimestamp} onCheckedChange={handleTimestampToggle} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="operator" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Operator Settings</CardTitle>
                <CardDescription>Configure the operator display in your chat widget</CardDescription>
              </CardHeader>
              <CardContent>
                {chatbotId && chatbot && (
                  <OperatorSettings
                    chatbotId={chatbotId}
                    operatorName={chatbot.operator_name}
                    operatorAvatarUrl={chatbot.operator_avatar_url}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="company" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>Update your company details displayed in the chat widget</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...companyInfoForm}>
                  <form onSubmit={companyInfoForm.handleSubmit(onCompanyInfoSubmit)} className="space-y-6">
                    <div className="flex flex-col md:flex-row items-start gap-6">
                      <FormItem className="w-auto">
                        <FormLabel>Company Logo</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-4">
                            {(companyLogoUrl || (chatbot?.company_logo && !removeLogoOnSave)) ? (
                              <div className="relative group">
                                <img
                                  src={companyLogoUrl || chatbot?.company_logo || ''}
                                  alt="Company Logo Preview"
                                  className="h-16 w-16 object-contain border rounded bg-gray-100"
                                />
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={removeCompanyLogo}
                                  type="button"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="h-16 w-16 border rounded bg-gray-100 flex items-center justify-center text-gray-400">
                                <ImageIcon size={24} />
                              </div>
                            )}
                            <label htmlFor="company-logo-upload" className="cursor-pointer">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isUploading}
                                asChild
                                type="button"
                              >
                                <span>
                                  {isUploading ? "Uploading..." : "Upload Logo"}
                                </span>
                              </Button>
                              <input
                                id="company-logo-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleCompanyLogoUpload}
                                className="hidden"
                              />
                            </label>
                          </div>
                        </FormControl>
                      </FormItem>

                      <FormField
                        control={companyInfoForm.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>Company Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter company name" {...field} />
                            </FormControl>
                            <FormDescription>
                              This will be displayed in your chat widget
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={companyInfoForm.control}
                      name="tcpaLanguage"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>TCPA Consent Language</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Enter TCPA consent language (e.g., 'By providing my phone number, I consent to receive text messages from [Company Name]. Message and data rates may apply. Reply STOP to opt out.')" 
                              className="min-h-[100px]"
                              {...field} 
                            />
                          </FormControl>
                          <FormDescription>
                            Required consent language for phone number collection. This will be displayed when requesting phone numbers from users.
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                    <Button type="submit" disabled={isUploading}>
                      Save Company Info
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="delays" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Message Delay Settings</CardTitle>
                <CardDescription>Configure timing for chat interactions to optimize user experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Message Delay (ms)</Label>
                  <Slider
                    min={0}
                    max={5000}
                    step={100}
                    value={[messageDelay]}
                    onValueChange={handleMessageDelayChange}
                  />
                  <span className="text-sm text-gray-500">{messageDelay}ms</span>
                  <p className="text-xs text-gray-400">Time between bot messages appearing</p>
                </div>

                <div className="space-y-2">
                  <Label>Button Options Delay (ms)</Label>
                  <Slider
                    min={0}
                    max={5000}
                    step={100}
                    value={[buttonOptionsDelay]}
                    onValueChange={handleButtonOptionsDelayChange}
                  />
                  <span className="text-sm text-gray-500">{buttonOptionsDelay}ms</span>
                  <p className="text-xs text-gray-400">Delay before showing button options</p>
                </div>

                <div className="space-y-2">
                  <Label>No Response Delay (ms)</Label>
                  <Slider
                    min={0}
                    max={10000}
                    step={100}
                    value={[noResponseDelay]}
                    onValueChange={handleNoResponseDelayChange}
                  />
                  <span className="text-sm text-gray-500">{noResponseDelay}ms</span>
                  <p className="text-xs text-gray-400">Time to wait for user response before triggering fallback</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Live Preview</h3>
        <div className="border rounded-lg p-4 bg-gray-50 shadow-sm relative overflow-hidden" style={{ height: '600px' }}>
          <iframe
            key={`${messageDelay}-${buttonOptionsDelay}-${noResponseDelay}-${optionsHandlerDelay}-${localTheme.showTimestamp}-${chatbot?.company_name || ''}-${companyLogoUrl || ''}-${chatbot?.operator_avatar_url || ''}`}
            src={`/chat/${chatbotId}?theme=${encodeURIComponent(JSON.stringify(localTheme))}`}
            title="Live Chat Preview"
            className="w-full h-full border-none"
            style={{ borderRadius: '8px' }}
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">
          This preview shows the <strong>actual production chat widget</strong> - exactly how users will experience it.
        </p>
      </div>
    </div>
  );
};
